import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { EMPTY, Subject, catchError, filter, map, startWith, switchMap, tap } from "rxjs";
import { AlertsService, AlertView } from "../alerts.service";
import { AlertCardComponent } from "../alert-card/alert-card.component";

// What an alert's "Open in Admin" link opens: any alert until the purge, resolved ones included.
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-alert",
    templateUrl: "./alert.component.html",
    styleUrl: "./alert.component.scss",
    imports: [RouterLink, MatButtonModule, AlertCardComponent],
})
export class AlertComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly alertsService = inject(AlertsService);
    private readonly reloads = new Subject<void>();

    readonly alert = signal<AlertView | null>(null);
    readonly notFound = signal(false);
    readonly loadFailed = signal(false);

    constructor() {
        // The router reuses this component from /alerts/A to /alerts/B, so a later id or refresh cancels the request
        // before it: the alert shown is the one asked for last, and A's card goes before B is asked for.
        this.route.paramMap
            .pipe(
                map((params) => params.get("id")),
                filter((id): id is string => id !== null),
                tap(() => this.alert.set(null)),
                switchMap((id) =>
                    this.reloads.pipe(
                        startWith(undefined),
                        map(() => id),
                    ),
                ),
                switchMap((id) => {
                    this.notFound.set(false);
                    this.loadFailed.set(false);
                    return this.alertsService.get(id).pipe(
                        catchError((error: HttpErrorResponse) => {
                            this.alert.set(null);
                            // The API answers 400 for an id that is not a GUID, such as a link cut short when it was copied.
                            if (error.status === 404 || error.status === 400) {
                                this.notFound.set(true);
                            } else {
                                this.loadFailed.set(true);
                            }
                            return EMPTY;
                        }),
                    );
                }),
                takeUntilDestroyed(),
            )
            .subscribe((alert) => this.alert.set(alert));
    }

    refresh(): void {
        this.reloads.next();
    }
}
